import { ReactElement } from 'react';
import type { ActivityType, ProductKind } from 'types';
import { EmbeddedProduct } from './types';
import { SelectField, SelectFieldProps } from '@ff-it/form';
import { useOptions, ResponseHandler } from 'components';
import { RequestSuccess } from '@ff-it/api';
import { ProductTitle } from './ProductTitle';
import { StylesConfig, selectStyle } from '@ff-it/ui';

type ProductFieldProps = {
  type: ActivityType;
  kind?: ProductKind | ProductKind[];
  provider?: number;
  outlet_or_service_category?: number;
  pageSize?: number;
} & Omit<SelectFieldProps<EmbeddedProduct>, 'loadOptions' | 'options'>;

type OutletOrCategoryGroup = {
  label: string;
  options: EmbeddedProduct[];
};

const groupOutletsAndCategories: ResponseHandler = (result: RequestSuccess<any>): any[] => {
  const data: EmbeddedProduct[] = result.data.results;
  // products are returend ordered by similary (including cat)
  const groups = new Map<number, OutletOrCategoryGroup>();

  for (let i = 0; i < data.length; i++) {
    const product = data[i];
    const group = groups.get(product.outlet_or_service_category.id);
    if (!group) {
      const group = {
        label: product.outlet_or_service_category.name,
        options: [product],
      };
      groups.set(product.outlet_or_service_category.id, group);
    } else {
      group.options.push(product);
    }
  }

  return Array.from(groups.values());
};

export const styles: StylesConfig<any, any, any> = {
  ...selectStyle,
  groupHeading: (_base) => ({
    position: 'sticky',
    top: 0,
    padding: '0.3rem 1rem',
    backgroundColor: '#f2f2f2',
    fontSize: '14px',
  }),
  group: (_base) => ({
    borderTop: '1px solid #eee',
  }),
  option: (base, ctx) => ({
    ...base,
    padding: '0.5rem 1.25rem',
    fontStyle: ctx.data.name === '' ? 'italic' : undefined,
  }),
  menu: (base) => ({
    ...base,
    marginTop: undefined,
    marginBottom: undefined,
  }),
};

export const productFieldOptions = {
  getOptionLabel: ({ name }: EmbeddedProduct) => name || 'Generic product',
  formatOptionLabel: (product: EmbeddedProduct, { context }: { context: 'menu' | 'value' }) => {
    return context === 'menu' ? (
      <span data-testid={`product-${product.id}`}>{product.name || 'Generic product'}</span>
    ) : (
      <ProductTitle product={product} />
    );
  },
  getOptionValue: (a: EmbeddedProduct) => a.id.toString(),
  styles,
};

export const groupedProductFieldOptions = {
  getOptionLabel: productFieldOptions.getOptionLabel,
  getOptionValue: productFieldOptions.getOptionValue,
};

export function ProductField({
  type,
  kind,
  provider,
  pageSize,
  outlet_or_service_category,
  ...props
}: ProductFieldProps): ReactElement {
  const filter: Record<string, unknown> = { type, provider, outlet_or_service_category, disabled: false };

  if (kind) {
    if (Array.isArray(kind)) {
      filter['kind__in'] = kind.join(',');
    } else {
      filter['kind'] = kind;
    }
  }

  const loadOptions = useOptions<any>('supplier/products/', filter, {
    responseHandler: groupOutletsAndCategories,
    pageSize,
  });

  return <SelectField<EmbeddedProduct> {...props} loadOptions={loadOptions} {...productFieldOptions} />;
}
